/**
 * API Functions
 * Data fetching via Netlify Functions (/api/*)
 * Local dev uses `netlify dev`, which serves the same /api/* routes
 */

import { CONFIG, THRESHOLDS, isLocalhost, getCacheKey } from './config.js';
import { getCurrentBuoy, getFallbackBuoys, getCurrentRegionId, getCurrentAreaId, AppState, getEffectiveTideStationId } from './state.js';
import { getCachedData, setCachedData } from './cache.js';
import { analytics } from './analytics.js';
import { parseNOAATimestamp, parseISO, parseSunTimes, timestamp, toISO } from './time.js';
import { logger } from './logger.js';

// Track when each data source was last updated
export const dataTimestamps = {};

// ===========================================
// Helper Functions (DRY)
// ===========================================

/**
 * Fetch with configurable timeout and optional external abort signal
 * Extracts the repeated AbortController pattern
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options (may include signal for external abort)
 * @param {number} timeout - Timeout in milliseconds
 */
async function fetchWithTimeout(url, options = {}, timeout = THRESHOLDS.fetch.direct) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // If external signal provided, abort our controller when it aborts
    if (options.signal) {
        options.signal.addEventListener('abort', () => controller.abort());
    }

    try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
}

/**
 * True when fetch() itself rejected: offline, DNS, blocked by an extension,
 * or the connection dropped. Browsers surface all of these as a TypeError
 * ("Failed to fetch", "NetworkError when attempting to fetch resource.",
 * "Load failed"). A non-2xx response is NOT a network error.
 */
function isNetworkError(error) {
    return error instanceof TypeError;
}

/**
 * Report a data-fetch failure at the right severity.
 * Network failures are the client's environment, not a bug: recorded as a
 * Sentry warning so they stay countable without polluting the error stream.
 * Everything else (API error status, bad payload) is a real error.
 * @param {string} message - Stable message; Sentry groups on it
 * @param {Error} error - The caught error
 * @param {Object} extra - Request context (url, station, spot, ...)
 */
function reportFetchFailure(message, error, extra = {}) {
    const details = { message: error.message, name: error.name, ...extra };
    if (isNetworkError(error)) {
        logger.warnRemote(`${message} (network)`, {
            tags: { failure: 'network' },
            extra: details,
        });
        return;
    }
    logger.error(message, details);
}

/**
 * Fetch current water level from NOAA (via Netlify function)
 */
export async function fetchCurrentWaterLevel(stationId) {
    // Validate station ID
    if (!stationId) {
        logger.warn('No NOAA station ID provided for water level fetch');
        return null;
    }

    try {
        const response = await fetch(`${CONFIG.api.waterLevel}?station=${stationId}`);
        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();

        if (data.data && data.data.length > 0) {
            const value = parseFloat(data.data[0].v);
            if (isNaN(value)) {
                logger.warn(`Water level data invalid for station ${stationId}`);
                return null;
            }
            return {
                value,
                time: data.data[0].t,
                quality: data.data[0].q
            };
        }
        // Some stations don't have real-time water level sensors
        logger.warn(`No water level data available for station ${stationId}`);
        return null;
    } catch (error) {
        logger.warn('Water level fetch error:', error.message);
        return null;
    }
}

/**
 * Fetch tide predictions from NOAA
 * Uses referenceStation for subordinate stations that don't have direct predictions
 */
export async function fetchTidePredictions(stationId, days = 2) {
    // Use reference station for subordinate stations
    const effectiveStationId = stationId || getEffectiveTideStationId();

    const cacheKey = getCacheKey('tidePredictions');
    const cached = getCachedData(cacheKey);
    if (cached) {
        dataTimestamps.predictions = cached.timestamp;
        return { ...cached, data: cached.data };
    }

    try {
        // Validate station ID
        if (!effectiveStationId) {
            logger.warn('No NOAA station ID configured for this location');
            return null;
        }

        const response = await fetch(`${CONFIG.api.predictions}?station=${effectiveStationId}&days=${days}`);
        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();

        if (data.predictions && data.predictions.length > 0) {
            // NOTE: /api/predictions (noaa-predictions.mjs) requests
            // time_zone=lst_ldt, so p.t is a naive station-local
            // "YYYY-MM-DD HH:mm" string (see parseTideTime note in ui.js)
            const predictions = data.predictions.map(p => ({
                time: p.t,
                value: parseFloat(p.v),
                type: p.type
            }));
            setCachedData(cacheKey, predictions);
            dataTimestamps.predictions = timestamp();
            return { data: predictions, timestamp: timestamp(), fromCache: false };
        }
        throw new Error('No prediction data');
    } catch (error) {
        logger.error('Tide predictions fetch error:', error);
        const staleCache = getCachedData(cacheKey, true);
        if (staleCache) return staleCache;
        return null;
    }
}

/**
 * Fetch 24-hour tide curve from NOAA
 * Uses referenceStation for subordinate stations that don't have direct predictions
 */
export async function fetchTideCurve(stationId, options = {}) {
    // Use reference station for subordinate stations
    const effectiveStationId = stationId || getEffectiveTideStationId();

    const cacheKey = getCacheKey('tideCurve');
    const cached = getCachedData(cacheKey);
    if (cached) {
        dataTimestamps.curve = cached.timestamp;
        const curveWithDates = cached.data.map(p => ({
            time: parseISO(p.time),
            value: p.value
        }));
        return { ...cached, data: curveWithDates };
    }

    // Validate station ID
    if (!effectiveStationId) {
        logger.warn('No NOAA station ID configured for tide curve');
        return null;
    }

    try {
        const response = await fetchWithTimeout(
            `${CONFIG.api.curve}?station=${effectiveStationId}`,
            { signal: options.signal },
            THRESHOLDS.fetch.direct
        );
        if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();

        if (data.predictions && data.predictions.length > 0) {
            // Timestamp audit (2026-08): /api/curve (noaa-curve.mjs) proxies NOAA
            // with time_zone=lst_ldt, so p.t is a naive "YYYY-MM-DD HH:mm"
            // STATION-local string with no zone info. Parsing with isGMT=false
            // treats it as browser-local, which is only correct when the viewer
            // is in the station's timezone. Left unchanged (not unambiguous to
            // fix client-side); a proper fix is the backend requesting gmt.
            // Note: noaa-curve.mjs's DB fallback emits GMT-naive strings in the
            // same field, which this parse misreads by the local UTC offset.
            const curveData = data.predictions.map(p => ({
                time: toISO(parseNOAATimestamp(p.t, false)) || p.t.replace(' ', 'T'),
                value: parseFloat(p.v)
            }));
            setCachedData(cacheKey, curveData);
            dataTimestamps.curve = timestamp();
            const curveWithDates = curveData.map(p => ({ time: parseISO(p.time), value: p.value }));
            return { data: curveWithDates, timestamp: timestamp(), fromCache: false };
        }
        throw new Error('No curve data');
    } catch (error) {
        // Don't log AbortErrors - they're expected when switching locations
        if (error.name !== 'AbortError') {
            reportFetchFailure('Tide curve fetch error', error, {
                stationId: effectiveStationId,
            });
        }
        const staleCache = getCachedData(cacheKey, true);
        if (staleCache) {
            const curveWithDates = staleCache.data.map(p => ({
                time: parseISO(p.time),
                value: p.value
            }));
            return { ...staleCache, data: curveWithDates };
        }
        return null;
    }
}

/**
 * Fetch buoy data from NDBC (via Netlify function)
 * @param {string} buoyId - Optional buoy ID override. If not provided, uses current region's buoy.
 */
export async function fetchBuoyData(buoyId = null) {
    const targetBuoyId = buoyId || getCurrentBuoy();
    const fallbackBuoys = getFallbackBuoys();

    // Try primary buoy first, then fallbacks
    const buoysToTry = [targetBuoyId, ...fallbackBuoys];

    for (const currentBuoyId of buoysToTry) {
        try {
            const response = await fetch(`${CONFIG.api.buoy}?station=${currentBuoyId}`);
            const data = await response.json();
            if (data.error) throw new Error(data.error);
            return { ...data, buoyId: currentBuoyId };
        } catch (error) {
            logger.warn(`Buoy ${currentBuoyId} fetch failed:`, error.message);
            // Continue to next buoy in fallback list
        }
    }

    logger.error('All buoy data fetch attempts failed');
    analytics.dataFetchError('buoy', 'All buoys failed');
    return null;
}

/**
 * Fetch weather forecast from Weather.gov
 */
export async function fetchWeatherForecast(lat, lon) {
    const cacheKey = getCacheKey('weather');
    const cached = getCachedData(cacheKey);
    if (cached) {
        dataTimestamps.weather = cached.timestamp;
        return { ...cached, data: cached.data };
    }

    try {
        const response = await fetch(`${CONFIG.api.weather}?lat=${lat}&lon=${lon}`);
        const weatherData = await response.json();
        if (weatherData.error) throw new Error(weatherData.error);

        setCachedData(cacheKey, weatherData);
        dataTimestamps.weather = timestamp();
        return { data: weatherData, timestamp: timestamp(), fromCache: false };
    } catch (error) {
        logger.error('Weather fetch error:', error);
        analytics.dataFetchError('weather', error.message);
        const staleCache = getCachedData(cacheKey, true);
        if (staleCache) return staleCache;
        return null;
    }
}

/**
 * Fetch conditions for a spot by slug (new spot-based API)
 * Calls /api/get-conditions?spot={slug}, which returns { spot, conditions }
 * (unlike the station path, which returns the bare conditions object).
 * The response is normalized to the same { data, spot, timestamp, fromCache }
 * shape the station path produces so callers don't need to branch.
 *
 * NOTE: no spots.json fetch happens here — spot metadata comes from state
 * (loaded once at init), keeping the per-refresh hot path to a single call.
 *
 * @param {string} spotSlug - Spot slug (e.g., 'blacks-beach')
 * @param {Object} options - Options including optional abort signal
 */
async function fetchConditionsBySpot(spotSlug, options = {}) {
    // Cache key is per-slug (station cache keys are per region/station)
    const cacheKey = `tide-spot-${spotSlug}-conditions`;

    const cached = getCachedData(cacheKey);
    if (cached) {
        dataTimestamps.conditions = cached.timestamp;
        logger.log(`Conditions loaded for ${spotSlug} (cached)`);
        return cached;
    }

    const url = `${CONFIG.api.conditions}?spot=${encodeURIComponent(spotSlug)}`;

    try {
        const response = await fetchWithTimeout(url, { signal: options.signal }, THRESHOLDS.fetch.direct);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const payload = await response.json();

        // Normalize: spot path wraps conditions ({ spot, conditions }),
        // station path returns them bare
        const data = payload?.conditions ?? null;
        const spot = payload?.spot ?? null;

        if (!data) {
            // Spot exists but has no conditions yet — don't cache the miss
            logger.warn(`No conditions data for spot ${spotSlug} yet`);
            return { data: null, spot, timestamp: timestamp(), fromCache: false };
        }

        setCachedData(cacheKey, data);
        dataTimestamps.conditions = timestamp();

        // Report degradation if level > 0 (spot payload uses camelCase dataQuality)
        const degradation = data.dataQuality?.degradationLevel || 0;
        if (degradation > 0) {
            analytics.dataDegraded(spotSlug, degradation, data.dataQuality?.degradationReason);
        }

        logger.log(`Conditions loaded for ${spotSlug}`);
        return { data, spot, timestamp: timestamp(), fromCache: false };
    } catch (error) {
        if (error.name !== 'AbortError') {
            reportFetchFailure('Spot conditions fetch error', error, { url, spotSlug });
            analytics.dataFetchError('conditions', error.message);
        }

        // Return stale cache on error
        const staleCache = getCachedData(cacheKey, true);
        if (staleCache) {
            logger.log(`Conditions loaded for ${spotSlug} (stale cache due to error)`);
            return staleCache;
        }

        return null;
    }
}

/**
 * Fetch all conditions from centralized Supabase endpoint
 * This is the PRIMARY data source - returns pre-aggregated data including ocean rating
 * Uses localStorage caching with 5-minute TTL (matches Supabase poll-conditions refresh rate)
 *
 * @param {string} regionId - Region ID (optional, defaults to current)
 * @param {string} areaId - Area ID (optional, defaults to current)
 * @param {string} stationId - Station ID (optional, defaults to current)
 * @param {Object} options - Options: { signal, spotSlug }. When spotSlug is
 *        provided, the spot-based endpoint is used instead of the station path.
 */
export async function fetchConditions(regionId = null, areaId = null, stationId = null, options = {}) {
    // Spot-based path (new): /api/get-conditions?spot={slug}
    if (options.spotSlug) {
        return fetchConditionsBySpot(options.spotSlug, options);
    }

    const region = regionId || getCurrentRegionId();
    const area = areaId || getCurrentAreaId();
    const station = stationId || AppState.currentStationId;

    // Build cache key for conditions
    const cacheKey = `tide-${region}-${station}-conditions`;

    // Check cache first (5-minute TTL matches Supabase refresh)
    const cached = getCachedData(cacheKey);
    if (cached) {
        dataTimestamps.conditions = cached.timestamp;
        logger.log('Conditions loaded (cached)');
        return cached;
    }

    try {
        const url = `${CONFIG.api.conditions}?region=${region}&area=${area}&station=${station}`;
        const response = await fetchWithTimeout(url, { signal: options.signal }, THRESHOLDS.fetch.direct);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const data = await response.json();

        // Cache the conditions data
        setCachedData(cacheKey, data);
        dataTimestamps.conditions = timestamp();

        // Report degradation if level > 0
        if (data.degradation_level && data.degradation_level > 0) {
            analytics.dataDegraded(station, data.degradation_level, data.degradation_reason);
        }

        logger.log('Conditions loaded');
        return {
            data,
            timestamp: timestamp(),
            fromCache: false
        };
    } catch (error) {
        // Don't log abort errors - they're expected when location changes
        if (error.name !== 'AbortError') {
            reportFetchFailure('Conditions fetch error', error, {
                url: `${CONFIG.api.conditions}?region=${region}&area=${area}&station=${station}`,
                station,
                region,
                area,
            });
            // Report error to GA4
            analytics.dataFetchError('conditions', error.message);
        }

        // Return stale cache on error
        const staleCache = getCachedData(cacheKey, true);
        if (staleCache) {
            logger.log('Conditions loaded (stale cache due to Supabase error)');
            return staleCache;
        }

        // If no cache, try per-source API fallback (local development)
        // This builds a minimal conditions object from the individual /api/* endpoints
        if (isLocalhost) {
            logger.warn('Supabase unavailable, attempting direct API fallback...');
            try {
                const directData = await fetchConditionsDirectFallback(station);
                if (directData) {
                    setCachedData(cacheKey, directData);
                    return { data: directData, timestamp: timestamp(), fromCache: false, fallback: true };
                }
            } catch (fallbackError) {
                logger.error('Direct API fallback also failed:', fallbackError);
            }
        }

        return null;
    }
}

/**
 * Fallback function to fetch conditions from the per-source Netlify endpoints
 * (/api/water-level, /api/predictions, /api/weather) when the aggregated
 * Supabase-backed /api/get-conditions endpoint is unavailable
 */
async function fetchConditionsDirectFallback(stationId) {
    logger.log('Fetching conditions from per-source API endpoints...');

    const station = AppState.currentStation;
    if (!station) return null;

    try {
        // Fetch what we can in parallel
        const [waterLevelResult, predictionsResult, weatherResult] = await Promise.allSettled([
            fetchCurrentWaterLevel(stationId),
            fetchTidePredictions(stationId),
            fetchWeatherForecast(station.lat, station.lon)
        ]);

        const waterLevel = waterLevelResult.status === 'fulfilled' ? waterLevelResult.value?.data : null;
        const predictions = predictionsResult.status === 'fulfilled' ? predictionsResult.value?.data : null;
        const weather = weatherResult.status === 'fulfilled' ? weatherResult.value?.data : null;

        // Return a minimal conditions object
        return {
            waterLevel,
            predictions,
            weather,
            buoy: null, // Buoy data requires parsing, skip for fallback
            sunTimes: null,
            degradation_level: 2,
            degradation_reason: 'Supabase unavailable - using direct API fallback'
        };
    } catch (error) {
        logger.error('Direct fallback failed:', error);
        return null;
    }
}

/**
 * Fetch sun times from sunrise-sunset.org
 */
export async function fetchSunTimes(lat, lon) {
    const cacheKey = getCacheKey('sunTimes');
    const cached = getCachedData(cacheKey);
    if (cached) {
        dataTimestamps.sunTimes = cached.timestamp;
        return { ...cached, data: cached.data };
    }

    try {
        const response = await fetch(`${CONFIG.api.sun}?lat=${lat}&lon=${lon}`);
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        const sunData = parseSunTimes(data);
        if (!sunData) throw new Error('Invalid sun times response');

        setCachedData(cacheKey, sunData);
        dataTimestamps.sunTimes = timestamp();
        return { data: sunData, timestamp: timestamp(), fromCache: false };
    } catch (error) {
        logger.error('Sun times fetch error:', error);
        const staleCache = getCachedData(cacheKey, true);
        if (staleCache) return staleCache;
        return null;
    }
}
